import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CrudModule from "@/components/CrudModule";
import { MODULES } from "@/config/modules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, ScanSearch, Link2, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUniversalIntake } from "@/context/UniversalIntakeContext";
import CarrierTruthPanel from "@/components/CarrierTruthPanel";
import FollowUpNowDialog from "@/components/FollowUpNowDialog";
import { SophiaSelectedCallDialog } from "@/components/SophiaSelectedCallDialog";
import AIBacklogStatus from "@/components/AIBacklogStatus";
import SophiaAcceptance from "@/components/SophiaAcceptance";

const SOPHIA_SELECT_ROLES = ["admin", "ops_manager", "agent", "bpo_closer"];

const QUICK_FILTERS = [
  { key: "historical", label: "Historical Follow-Up Queue", test: (r) => r.source === "Historical A.POL Import" || r.followup_type === "Historical Follow-Up" },
  { key: "active", label: "Active Retention", test: (r) => r.source !== "Historical A.POL Import" && r.followup_type !== "Historical Follow-Up" },
  // Section K: queue filters. Readiness comes from the last "Refresh AI Readiness"
  // (POST /voice-ai/auto/triage), which writes triage_readiness onto each record.
  { key: "ai_ready", label: "AI Ready", test: (r) => r.triage_readiness === "AI_READY" },
  { key: "urgent_ai_ready", label: "Urgent AI Ready", test: (r) => r.triage_readiness === "URGENT_AI_READY" },
  { key: "ai_permission_required", label: "AI Permission Required", test: (r) => r.triage_readiness === "AI_PERMISSION_REQUIRED" },
  { key: "carrier_verification_required", label: "Carrier Verification Required", test: (r) => r.triage_readiness === "CARRIER_VERIFICATION_REQUIRED" },
  { key: "missing_contact", label: "Missing Contact", test: (r) => r.triage_readiness === "MISSING_CONTACT" },
  { key: "no_outbound", label: "No Outbound", test: (r) => r.no_outbound === true || r.triage_readiness === "NO_OUTBOUND" },
  { key: "held", label: "Held", test: (r) => r.sophia_hold === true },
  { key: "scheduled", label: "Scheduled", test: (r) => r.automation_state === "queued" },
  { key: "in_progress", label: "In Progress", test: (r) => r.automation_state === "ai_call_in_progress" },
  { key: "completed", label: "Completed", test: (r) => r.automation_state === "ai_call_completed" },
  { key: "human_review", label: "Human Review", test: (r) => r.sophia_human_review_required === true || r.automation_state === "needs_human_followup" },
];

const READINESS_LABELS = {
  AI_READY: "AI ready",
  URGENT_AI_READY: "Urgent AI",
  AI_PERMISSION_REQUIRED: "AI permission",
  CARRIER_VERIFICATION_REQUIRED: "Carrier verify",
  AGENT_REVIEW_REQUIRED: "Agent review",
  MISSING_CONTACT: "Missing contact",
  NO_OUTBOUND: "No outbound",
};

const POWER_DIALER_ROLES = ["admin", "ops_manager", "agent", "bpo_closer"];

export default function FollowUps() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openIntake } = useUniversalIntake();
  const canPowerDial = POWER_DIALER_ROLES.includes(user?.role);
  const [actingOn, setActingOn] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [triageBusy, setTriageBusy] = useState(false);
  const [triageSummary, setTriageSummary] = useState(null);
  const [permissionBusy, setPermissionBusy] = useState(false);
  const [permissionBasis, setPermissionBasis] = useState("");
  const [permissionConfirmed, setPermissionConfirmed] = useState(false);
  const [linkReview, setLinkReview] = useState(null);
  const [linkChoices, setLinkChoices] = useState({});
  const [linkBusy, setLinkBusy] = useState(false);
  const [sophiaSelectedIds, setSophiaSelectedIds] = useState(null);
  const canTriage = ["admin", "ops_manager"].includes(user?.role);
  const canSophiaSelect = SOPHIA_SELECT_ROLES.includes(user?.role);

  const refreshReadiness = async () => {
    if (!canTriage) return;
    setTriageBusy(true);
    try {
      const { data } = await api.post("/voice-ai/auto/triage");
      setTriageSummary(data);
      setRefreshKey((x) => x + 1);
      toast.success(`Reviewed ${data.total || 0} follow-ups · ${data.reclassified || 0} reclassified · ${data.exceptions_open || 0} human exception(s)`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not refresh Sophia readiness");
    } finally {
      setTriageBusy(false);
    }
  };

  const grantReviewedCohort = async () => {
    if (!canTriage || !permissionConfirmed || permissionBasis.trim().length < 12) return;
    setPermissionBusy(true);
    try {
      const { data } = await api.post("/voice-ai/auto/permission-grant-bulk", {
        basis: permissionBasis.trim(),
        confirm_basis_applies: true,
      });
      toast.success(`AI voice authorization recorded for ${data.granted || 0} reviewed follow-up(s); ${data.skipped || 0} skipped`);
      setPermissionConfirmed(false);
      await refreshReadiness();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not record AI voice authorization");
    } finally {
      setPermissionBusy(false);
    }
  };

  const openLinkReview = async () => {
    setLinkBusy(true);
    try {
      const [{ data: followups }, { data: clients }] = await Promise.all([api.get("/followups"), api.get("/clients")]);
      setLinkReview({ rows: followups.filter((row) => row.link_status === "pending_review"), clients });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load pending links");
    } finally { setLinkBusy(false); }
  };

  const approveLinks = async () => {
    const items = Object.entries(linkChoices).filter(([, clientId]) => clientId).map(([followupId, clientId]) => ({ followup_id: followupId, client_id: clientId }));
    if (!items.length) return;
    setLinkBusy(true);
    try {
      const { data } = await api.post("/followups/bulk-link", { items });
      toast.success(`Linked ${data.linked} imported follow-up(s)`);
      setLinkReview(null); setLinkChoices({}); setRefreshKey((x) => x + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve links");
    } finally { setLinkBusy(false); }
  };

  return (
    <>
      <SophiaAcceptance />
      <AIBacklogStatus />
      <div className="mb-4">
        <CarrierTruthPanel defaultCarrier="Fidelity" />
      </div>
      {triageSummary && (
        <div className="mb-4 rounded-lg border bg-card px-4 py-3 text-xs" data-testid="followups-triage-summary">
          <div className="font-semibold">Sophia readiness · {triageSummary.total || 0} open follow-ups</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(triageSummary.by_readiness || {}).map(([key, value]) => (
              <span key={key} className="rounded-full bg-secondary px-2.5 py-1">
                {READINESS_LABELS[key] || key}: <strong>{value}</strong>
              </span>
            ))}
            <span className="rounded-full bg-secondary px-2.5 py-1">Reclassified: <strong>{triageSummary.reclassified || 0}</strong></span>
            <span className="rounded-full bg-secondary px-2.5 py-1">Human exceptions: <strong>{triageSummary.exceptions_open || 0}</strong></span>
          </div>
        </div>
      )}
      {canTriage && (triageSummary?.by_readiness?.AI_PERMISSION_REQUIRED || 0) > 0 && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/60 p-4 text-xs" data-testid="ai-permission-cohort">
          <div className="font-semibold text-violet-950">{triageSummary.by_readiness.AI_PERMISSION_REQUIRED} otherwise-ready follow-up(s) need documented AI/artificial-voice authorization.</div>
          <p className="mt-1 text-violet-900/80">This does not bypass DNC and does not start calls. Only use this when one documented authorization basis genuinely applies to the whole reviewed cohort.</p>
          <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto]">
            <Input value={permissionBasis} onChange={(e) => setPermissionBasis(e.target.value)} placeholder="Documented basis / consent reference that applies to this cohort" data-testid="ai-permission-basis" />
            <Button variant="outline" onClick={grantReviewedCohort} disabled={permissionBusy || !permissionConfirmed || permissionBasis.trim().length < 12}>
              {permissionBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Record Authorization
            </Button>
          </div>
          <label className="mt-2 flex items-start gap-2 text-violet-950">
            <input type="checkbox" checked={permissionConfirmed} onChange={(e) => setPermissionConfirmed(e.target.checked)} />
            <span>I confirm this documented authorization basis applies to this cohort for AI/artificial-voice service calls.</span>
          </label>
        </div>
      )}
      {linkReview && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-xs" data-testid="followup-link-review">
          <div className="font-semibold">Review imported client links · {linkReview.rows.length} pending</div>
          <div className="mt-3 space-y-2">
            {linkReview.rows.map((row) => (
              <div key={row.id} className="grid items-center gap-2 md:grid-cols-[1fr_1fr]">
                <span>{row.client_name || "Unnamed"} · {row.policy_number || "No policy #"}</span>
                <select className="h-9 rounded-md border bg-background px-2" value={linkChoices[row.id] || ""} onChange={(e) => setLinkChoices((old) => ({ ...old, [row.id]: e.target.value }))}>
                  <option value="">Select verified client…</option>
                  {linkReview.clients.map((client) => <option key={client.id} value={client.id}>{client.client_name} · {client.phone || client.email || client.id}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2"><Button onClick={approveLinks} disabled={linkBusy}>Approve Selected Links</Button><Button variant="outline" onClick={() => setLinkReview(null)}>Close</Button></div>
        </div>
      )}
      <CrudModule
        module={MODULES.followups}
        subtitle="Retention & historical case follow-ups — carrier truth sets Sophia's service reason; only callable, permitted, non-suppressed records can be worked automatically."
        quickFilters={QUICK_FILTERS}
        refreshKey={refreshKey}
        rowAction={{
          label: "Follow Up Now",
          position: "start",
          testid: "follow-up-now",
          headerLabel: "Work Next",
          headerTestid: "follow-up-work-next",
          headerClassName: "brand-action shadow-sm",
          className: "border-gold bg-gold/10 text-primary hover:bg-gold/20",
          onClick: setActingOn,
        }}
        selectionAction={canSophiaSelect ? {
          label: "Call Selected with Sophia",
          testid: "call-selected-sophia",
          onClick: (ids) => setSophiaSelectedIds(ids),
        } : null}
        headerExtra={
          <div className="flex flex-wrap gap-2">
            {canPowerDial && (
              <Button variant="outline" onClick={() => navigate("/telephony")} data-testid="open-power-dialer">
                <PhoneCall className="mr-1.5 h-4 w-4" /> Power Dialer
              </Button>
            )}
            {canTriage && (
              <Button variant="outline" onClick={openLinkReview} disabled={linkBusy} data-testid="review-import-links">
                {linkBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />}
                Review Imported Links
              </Button>
            )}
            {canTriage && (
              <Button variant="outline" onClick={refreshReadiness} disabled={triageBusy} data-testid="refresh-ai-readiness">
                {triageBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />}
                Refresh AI Readiness
              </Button>
            )}
            <Button className="brand-action" data-testid="followups-universal-intake" onClick={() => openIntake()}>
              <ScanSearch className="mr-1.5 h-4 w-4" /> Universal Intake
            </Button>
          </div>
        }
      />
      <FollowUpNowDialog
        row={actingOn}
        open={!!actingOn}
        onOpenChange={(open) => !open && setActingOn(null)}
        onUpdated={() => setRefreshKey((x) => x + 1)}
      />
      <SophiaSelectedCallDialog
        followupIds={sophiaSelectedIds}
        open={!!sophiaSelectedIds}
        onOpenChange={(open) => !open && setSophiaSelectedIds(null)}
        onLaunched={() => setRefreshKey((x) => x + 1)}
      />
    </>
  );
}
